import { Text } from "@/components/ui/baseUi/text"
import { AddSessionButton } from "@/modules/calendar/components/addSessionButton"
import { cn } from "@/modules/lib/utils"
import { addEvents, type DNDPlugin, parents } from "@formkit/drag-and-drop"
import { dragAndDrop } from "@formkit/drag-and-drop/react"
import { type EventPerDay } from "@modules/calendar/calendar"
import { type EventType } from "@modules/calendar/calendarTypes"
import { DefaultCalendarEvent } from "@modules/calendar/components/defaultCalendarEvent"
import { useCalendar } from "@modules/calendar/hooks/useCalendar"
import { word } from "@modules/core/utils/i18n"
import { dateSeparator, defaultDateFormat } from "@modules/utils/dateUtils"
import dayjs from "dayjs"
import { useEffect, useEffectEvent, useMemo, useRef, useState } from "react"
import styled from "styled-components"

type CalendarDayColumnProps = {
  eventsPerDay: EventPerDay
  customRenderEvent?: (index: number, event: EventType, displayDetailedEvents: boolean) => React.ReactNode
  onAddEventClick?: (arg0?: Date | number) => void
  index: number
  canAddEvents: boolean
  eventLimitPerDay: number
  onEventDragStop?: (dateDest?: dayjs.Dayjs, position?: number) => Promise<void>
  isDragAndDropActive: boolean
} & React.ComponentProps<"div">

export function CalendarDayColumn({
  eventsPerDay,
  customRenderEvent,
  onAddEventClick,
  canAddEvents,
  eventLimitPerDay,
  onEventDragStop,
  isDragAndDropActive,
  className,
}: CalendarDayColumnProps) {
  const [items, setItems] = useState(eventsPerDay.events)

  const { displayDetailedEvents, isUndated, maxDayIndex, draggingEvent, setIsDragging, isDragging } = useCalendar()

  const dragStatusPlugin: DNDPlugin = (parent) => {
    const parentData = parents.get(parent)
    if (!parentData) return

    return {
      setupNode({ node }) {
        const controller = addEvents(node.el, {
          dragend: () => {
            setIsDragging(false)
          },
        })

        // optional: clear any old controller for this key first
        node.data.abortControllers.customPlugin?.abort()
        node.data.abortControllers.customPlugin = controller
      },
      tearDownNode({ node }) {
        node.data?.abortControllers.customPlugin?.abort()
      },
    }
  }

  const isLimitPerDayReached = useMemo(
    () => eventsPerDay.events.length >= eventLimitPerDay,
    [eventsPerDay.events.length, eventLimitPerDay],
  )

  const canAddEventOnDay = useMemo(() => {
    return (
      canAddEvents &&
      ((isUndated &&
        typeof eventsPerDay.dayIndex !== "undefined" &&
        ((maxDayIndex && eventsPerDay.dayIndex < maxDayIndex) || maxDayIndex === undefined)) ||
        !isUndated)
    )
  }, [eventsPerDay, canAddEvents, eventLimitPerDay, isUndated, maxDayIndex])

  const dateSession = eventsPerDay.date?.toDate() ?? eventsPerDay.dayIndex
  const parentRef = useRef<HTMLDivElement>(null)

  const setIemsEvent = useEffectEvent(() => {
    setItems(eventsPerDay.events)
  })

  useEffect(() => {
    if (!draggingEvent) {
      setIemsEvent()
    }
  }, [eventsPerDay.events, draggingEvent])

  useEffect(() => {
    if (!parentRef.current || isDragging) return
    dragAndDrop({
      parent: parentRef,
      state: [items, setItems],
      plugins: [dragStatusPlugin],
      group: "calendarEvents",
      disabled: !isDragAndDropActive,
    })
  }, [items.length, isDragAndDropActive, isDragging])

  useEffect(() => {
    if (!isDragging && draggingEvent && items.findIndex((item) => item.id === draggingEvent.id) >= 0) {
      const position = items.findIndex((item) => item.id === draggingEvent.id)
      void onEventDragStop?.(eventsPerDay.date, position)
    }
  }, [isDragging, draggingEvent])

  return (
    <Container
      id={`${dateSeparator}${
        eventsPerDay.date
          ? dayjs(eventsPerDay.date).format(defaultDateFormat)
          : eventsPerDay.dayIndex
            ? eventsPerDay.dayIndex.toString()
            : ""
      }`}
      className={cn("dayCell pt-2 pr-0 pl-0 lg:pr-1 lg:pl-1", { dragging: isDragging }, eventsPerDay.class, className)}
      style={{
        backgroundColor:
          isUndated && eventsPerDay.dayIndex && maxDayIndex && eventsPerDay.dayIndex >= maxDayIndex
            ? "var(--neutral-50)"
            : "transparent",
      }}
    >
      <div ref={parentRef} style={{ minHeight: isDragging ? 460 : "auto" }}>
        {items.map((event, eventIndex) =>
          typeof customRenderEvent !== "undefined" ? (
            customRenderEvent(eventIndex, event, displayDetailedEvents)
          ) : (
            <DefaultCalendarEvent
              key={event.id}
              index={eventIndex}
              displayDetailedEvents={displayDetailedEvents}
              event={event}
            />
          ),
        )}
      </div>
      {canAddEventOnDay &&
        onAddEventClick &&
        !isDragging &&
        (isLimitPerDayReached ? (
          <div className="limit-reached flex cursor-pointer flex-col justify-center text-center">
            <Text fontStyle="BS" className="text-textError">
              {word("sessionForm.create.daily_limit_reached")}
            </Text>
          </div>
        ) : (
          typeof dateSession !== "undefined" && (
            <AddSessionButton
              dateSession={dateSession}
              className={cn(items.length > 0 && "mt-2")}
              onAddEventClick={onAddEventClick}
            />
          )
        ))}
    </Container>
  )
}

const Container = styled.td`
  vertical-align: top;
  text-align: left;
  word-break: break-word;
  &.past .limit-reached {
    display: none;
  }
  > .add {
    opacity: 0;
    transition: opacity 0.2s ease;
    cursor: pointer;
    gap: 10px;
  }
  > .limit-reached {
    opacity: 0;
    transition: opacity 0.2s ease;
    margin-top: 20px;
    gap: 10px;
  }
  &:not(.dragging):hover .add:not(.add-hidden),
  &:not(.dragging):hover .limit-reached {
    opacity: 1;
  }
`
